import { can, canSeeLoadRates, retrieveCustomization } from '../../supplementary_components/userDataDelegate';

/** Short ids stored in employee.customizations.pageLayouts.truck. */
export const TRUCK_BLOCK = {
    PERF: 'perf',
    LOADS: 'loads',
    TASKS: 'tasks',
    INVOICES: 'invoices',
    ACTIVITY: 'activity',
};

/** Catalog order is the append order for a block a saved layout has never mentioned. */
export const TRUCK_BLOCK_CATALOG = [
    TRUCK_BLOCK.PERF,
    TRUCK_BLOCK.LOADS,
    TRUCK_BLOCK.TASKS,
    TRUCK_BLOCK.INVOICES,
    TRUCK_BLOCK.ACTIVITY,
];

const DEFAULT_ORDER = [
    TRUCK_BLOCK.PERF,
    TRUCK_BLOCK.LOADS,
    TRUCK_BLOCK.TASKS,
    TRUCK_BLOCK.INVOICES,
    TRUCK_BLOCK.ACTIVITY,
];

const FLEET_ORDER = [
    TRUCK_BLOCK.INVOICES,
    TRUCK_BLOCK.PERF,
    TRUCK_BLOCK.LOADS,
    TRUCK_BLOCK.TASKS,
    TRUCK_BLOCK.ACTIVITY,
];

export const TRUCK_BLOCK_LABELS = {
    [TRUCK_BLOCK.PERF]: "This Week's Performance",
    [TRUCK_BLOCK.LOADS]: 'Latest Loads',
    [TRUCK_BLOCK.TASKS]: 'Task List',
    [TRUCK_BLOCK.INVOICES]: 'Latest Maintenance Invoices',
    [TRUCK_BLOCK.ACTIVITY]: 'Latest Activities',
};

export const truckPresetOrder = (roleCode, roleName) => (
    usesFleetTruckLayout(roleCode, roleName) ? [...FLEET_ORDER] : [...DEFAULT_ORDER]
);

const hasWord = (value, word) => {
    if (value == null || value === '') return false;
    return String(value).toLowerCase().split(/[^a-z0-9]+/).includes(word);
};

/** Fleet when the role code contains the word, otherwise when the role name does. */
export const usesFleetTruckLayout = (roleCode, roleName) =>
    hasWord(roleCode, 'fleet') || hasWord(roleName, 'fleet');

export const canSeeTruckBlock = (id) => {
    if (id === TRUCK_BLOCK.PERF) return canSeeLoadRates();
    if (id === TRUCK_BLOCK.INVOICES) return can('PAYABLE_INVOICES', 'VIEW');
    return true;
};

const knownIds = (list, catalog) => {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    const ids = [];
    list.forEach((id) => {
        if (typeof id !== 'string' || !catalog.includes(id) || seen.has(id)) return;
        seen.add(id);
        ids.push(id);
    });
    return ids;
};

/**
 * Saved layout is `{ order?, hidden? }` of short ids. Missing order uses the role preset.
 * Ids in neither the chosen order nor hidden are appended, then access removes the rest.
 */
export const resolveTruckBlockIds = ({
    roleCode,
    roleName,
    saved,
    canSeeBlock = canSeeTruckBlock,
    catalog = TRUCK_BLOCK_CATALOG,
} = {}) => {
    const preset = usesFleetTruckLayout(roleCode, roleName) ? FLEET_ORDER : DEFAULT_ORDER;
    const hidden = new Set(knownIds(saved?.hidden, catalog));
    const savedOrder = knownIds(saved?.order, catalog);
    const base = savedOrder.length > 0 ? savedOrder : preset.filter((id) => catalog.includes(id));
    const seen = new Set();
    const ordered = [];
    const push = (id) => {
        if (!catalog.includes(id) || seen.has(id) || hidden.has(id)) return;
        if (!canSeeBlock(id)) return;
        seen.add(id);
        ordered.push(id);
    };
    base.forEach(push);
    catalog.forEach(push);
    return ordered;
};

export const readSavedTruckPageLayout = () => {
    const layouts = retrieveCustomization('pageLayouts');
    const truck = layouts && typeof layouts === 'object' && !Array.isArray(layouts) ? layouts.truck : null;
    if (!truck || typeof truck !== 'object' || Array.isArray(truck)) return null;
    return {
        order: Array.isArray(truck.order) ? truck.order : null,
        hidden: Array.isArray(truck.hidden) ? truck.hidden : null,
    };
};

const sameIds = (left, right) => left.length === right.length && left.every((id, index) => id === right[index]);

/** Blocks this person can show or hide, in the order the editor should open with. */
export function editorTruckBlocks({
    roleCode,
    roleName,
    saved,
    canSeeBlock = canSeeTruckBlock,
    catalog = TRUCK_BLOCK_CATALOG,
} = {}) {
    const accessible = catalog.filter((id) => canSeeBlock(id));
    const hidden = new Set(knownIds(saved?.hidden, catalog).filter((id) => accessible.includes(id)));
    const savedOrder = knownIds(saved?.order, catalog).filter((id) => accessible.includes(id) && !hidden.has(id));
    const preset = truckPresetOrder(roleCode, roleName).filter((id) => accessible.includes(id) && !hidden.has(id));
    const base = savedOrder.length > 0 ? savedOrder : preset;
    const shown = [];
    const seen = new Set();
    const pushShown = (id) => {
        if (!accessible.includes(id) || hidden.has(id) || seen.has(id)) return;
        seen.add(id);
        shown.push({ id, shown: true });
    };
    base.forEach(pushShown);
    accessible.forEach(pushShown);
    const hiddenItems = accessible.filter((id) => hidden.has(id)).map((id) => ({ id, shown: false }));
    return [...shown, ...hiddenItems];
}

/**
 * Null when the list matches the role layout. Otherwise only `order` and/or `hidden`.
 * `order` is omitted when hiding alone still leaves the role sequence.
 */
export function truckLayoutSaveValue({
    items,
    roleCode,
    roleName,
    canSeeBlock = canSeeTruckBlock,
    catalog = TRUCK_BLOCK_CATALOG,
} = {}) {
    const accessible = new Set(catalog.filter((id) => canSeeBlock(id)));
    const visibleOrder = [];
    const hidden = [];
    (items || []).forEach((item) => {
        if (!item || !accessible.has(item.id)) return;
        if (item.shown) visibleOrder.push(item.id);
        else hidden.push(item.id);
    });
    const implied = [];
    const seen = new Set();
    const push = (id) => {
        if (!accessible.has(id) || hidden.includes(id) || seen.has(id)) return;
        seen.add(id);
        implied.push(id);
    };
    truckPresetOrder(roleCode, roleName).forEach(push);
    catalog.forEach(push);
    const value = {};
    if (!sameIds(visibleOrder, implied)) value.order = visibleOrder;
    if (hidden.length > 0) value.hidden = hidden;
    return Object.keys(value).length > 0 ? value : null;
}

/** Replace one page inside pageLayouts. Null page value removes it. Null result clears the key. */
export function mergePageLayout(existingLayouts, pageKey, pageValue) {
    const current = existingLayouts && typeof existingLayouts === 'object' && !Array.isArray(existingLayouts)
        ? { ...existingLayouts }
        : {};
    if (pageValue == null) delete current[pageKey];
    else current[pageKey] = pageValue;
    return Object.keys(current).length > 0 ? current : null;
}

export const resolveTruckOverviewSection = (blockById) => {
    const ids = resolveTruckBlockIds({
        roleCode: typeof localStorage !== 'undefined' ? localStorage.getItem('roleCode') : '',
        roleName: typeof localStorage !== 'undefined' ? localStorage.getItem('roleName') : '',
        saved: readSavedTruckPageLayout(),
    });
    return ids.map((id) => blockById[id]).filter(Boolean);
};
